import * as React from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useLocale } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import { useAuth } from "@/lib/auth"
import type { AdminUserRow } from "@/lib/types"
import { formatDateTime } from "@/lib/utils"

export function AdminUsersPage() {
  const { user: me } = useAuth()
  const { locale } = useLocale()
  const [rows, setRows] = React.useState<AdminUserRow[]>([])
  const [registrationOpen, setRegistrationOpen] = React.useState<boolean | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [busy, setBusy] = React.useState<string | null>(null)

  const load = React.useCallback(() => {
    setLoading(true)
    Promise.all([api.adminUsers(200, 0), api.authConfig()])
      .then(([page, config]) => {
        setRows(page.items)
        setRegistrationOpen(config.allow_registration)
      })
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : "Could not load users."),
      )
      .finally(() => setLoading(false))
  }, [])

  React.useEffect(load, [load])

  async function toggle(row: AdminUserRow) {
    setBusy(row.id)
    try {
      await api.adminSetUserActive(row.id, !row.is_active)
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, is_active: !r.is_active } : r)))
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Could not update the user.")
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader title="Users" description="Every account on the platform." />
      <div className="space-y-4 p-6">
        <p className="text-muted-foreground text-sm">
          Registration is{" "}
          {registrationOpen == null ? "…" : registrationOpen ? "open" : "closed"}.
        </p>
        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Email</th>
                  <th className="px-4 py-2 text-left font-medium">Name</th>
                  <th className="px-4 py-2 text-left font-medium">Agents</th>
                  <th className="px-4 py-2 text-left font-medium">Runs</th>
                  <th className="px-4 py-2 text-left font-medium">Plan</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  <th className="px-4 py-2 text-left font-medium">Joined</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="px-4 py-2">
                      <Link
                        to={`/admin/users/${row.id}`}
                        className="flex items-center gap-2 hover:underline"
                      >
                        {row.email}
                        {row.is_admin && <Badge variant="secondary">admin</Badge>}
                      </Link>
                    </td>
                    <td className="px-4 py-2">{row.display_name ?? "—"}</td>
                    <td className="px-4 py-2">{row.agents}</td>
                    <td className="px-4 py-2">{row.runs}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline">{row.plan_key}</Badge>
                    </td>
                    <td className="px-4 py-2">
                      {row.is_active ? (
                        <Badge variant="success">active</Badge>
                      ) : (
                        <Badge variant="outline">inactive</Badge>
                      )}
                    </td>
                    <td className="text-muted-foreground px-4 py-2">
                      {formatDateTime(row.created_at, locale)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy === row.id || row.id === me?.id}
                        title={row.id === me?.id ? "You cannot deactivate yourself" : undefined}
                        onClick={() => toggle(row)}
                      >
                        {row.is_active ? "Deactivate" : "Activate"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
